import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DayLog, Note, NoteInput, Project, Task, TaskInput } from "./api";
import { todayKey } from "./date";
import { deleteRecord, enqueue, getAll, getRecord, putRecord } from "./idb";
import { requestSync } from "./syncController";

const nowIso = () => new Date().toISOString();
const uid = () => crypto.randomUUID();

/* ------------- reads (from the local mirror) ------------- */
export function useTasks() {
  return useQuery({ queryKey: ["tasks"], queryFn: () => getAll<Task>("tasks") });
}
export function useProjects() {
  return useQuery({ queryKey: ["projects"], queryFn: () => getAll<Project>("projects") });
}
export function useDayLogs() {
  return useQuery({ queryKey: ["dayLogs"], queryFn: () => getAll<DayLog>("dayLogs") });
}
export function useNotes() {
  return useQuery({ queryKey: ["notes"], queryFn: () => getAll<Note>("notes") });
}

/* ------------- local-first writes ------------- */
function taskData(t: Task): Record<string, unknown> {
  const {
    id: _id,
    organizationId: _o,
    userId: _u,
    createdAt: _c,
    updatedAt: _up,
    ...rest
  } = t;
  return rest;
}

async function createTaskLocal(input: TaskInput & { title: string; id?: string }): Promise<Task> {
  const iso = nowIso();
  const task: Task = {
    id: input.id ?? uid(),
    organizationId: "",
    userId: "",
    projectId: input.projectId ?? null,
    title: input.title,
    pri: input.pri ?? 2,
    estMin: input.estMin ?? null,
    actualMin: input.actualMin ?? null,
    energy: input.energy ?? null,
    doDate: input.doDate ?? null,
    fromHour: input.fromHour ?? null,
    deadline: input.deadline ?? null,
    status: input.status ?? "todo",
    mit: input.mit ?? false,
    // A star with no explicit day is a Top-3 for its planned day (or today).
    mitDate: input.mitDate ?? (input.mit ? (input.doDate ?? todayKey()) : null),
    sort: input.sort ?? 0,
    completedAt: input.status === "done" ? iso : null,
    createdAt: iso,
    updatedAt: iso,
  };
  await putRecord("task", task);
  await enqueue({ entity: "task", type: "upsert", id: task.id, ts: Date.parse(iso), data: taskData(task) });
  requestSync();
  return task;
}

async function updateTaskLocal(id: string, patch: TaskInput): Promise<void> {
  const local = await getRecord<Task>("task", id);
  if (!local) return;
  const iso = nowIso();
  const merged: Task = { ...local, ...patch, updatedAt: iso };
  if (patch.status !== undefined) merged.completedAt = patch.status === "done" ? iso : null;
  await putRecord("task", merged);
  await enqueue({ entity: "task", type: "upsert", id, ts: Date.parse(iso), data: taskData(merged) });
  requestSync();
}

async function deleteTaskLocal(id: string): Promise<void> {
  await deleteRecord("task", id);
  await enqueue({ entity: "task", type: "delete", id, ts: Date.now() });
  requestSync();
}

function noteData(n: Note): Record<string, unknown> {
  return {
    projectId: n.projectId,
    taskId: n.taskId,
    title: n.title,
    content: n.content,
    excerpt: n.excerpt,
    pinned: n.pinned,
    dayDate: n.dayDate,
  };
}

async function createNoteLocal(input: NoteInput & { id?: string }): Promise<Note> {
  const iso = nowIso();
  const note: Note = {
    id: input.id ?? uid(),
    organizationId: "",
    userId: "",
    projectId: input.projectId ?? null,
    taskId: input.taskId ?? null,
    title: input.title ?? "",
    content: input.content ?? "",
    excerpt: input.excerpt ?? "",
    pinned: input.pinned ?? false,
    dayDate: input.dayDate ?? null,
    createdAt: iso,
    updatedAt: iso,
  };
  await putRecord("note", note);
  await enqueue({ entity: "note", type: "upsert", id: note.id, ts: Date.parse(iso), data: noteData(note) });
  requestSync();
  return note;
}

async function updateNoteLocal(id: string, patch: NoteInput): Promise<void> {
  const local = await getRecord<Note>("note", id);
  if (!local) return;
  const iso = nowIso();
  const merged: Note = { ...local, ...patch, updatedAt: iso };
  await putRecord("note", merged);
  await enqueue({ entity: "note", type: "upsert", id, ts: Date.parse(iso), data: noteData(merged) });
  requestSync();
}

async function deleteNoteLocal(id: string): Promise<void> {
  await deleteRecord("note", id);
  await enqueue({ entity: "note", type: "delete", id, ts: Date.now() });
  requestSync();
}

async function createProjectLocal(input: { name: string; color?: string }): Promise<Project> {
  const iso = nowIso();
  const project: Project = {
    id: uid(),
    name: input.name,
    color: input.color ?? "#7C96FF",
    visibility: "private",
    archived: false,
    createdAt: iso,
    updatedAt: iso,
  };
  await putRecord("project", project);
  await enqueue({
    entity: "project",
    type: "upsert",
    id: project.id,
    ts: Date.parse(iso),
    data: { name: project.name, color: project.color, visibility: project.visibility, archived: project.archived },
  });
  requestSync();
  return project;
}

async function updateProjectLocal(
  id: string,
  patch: Partial<Pick<Project, "name" | "color" | "visibility" | "archived">>,
): Promise<void> {
  const local = await getRecord<Project>("project", id);
  if (!local) return;
  const iso = nowIso();
  const merged: Project = { ...local, ...patch, updatedAt: iso };
  await putRecord("project", merged);
  await enqueue({
    entity: "project",
    type: "upsert",
    id,
    ts: Date.parse(iso),
    data: {
      name: merged.name,
      color: merged.color,
      visibility: merged.visibility,
      archived: merged.archived,
    },
  });
  requestSync();
}

async function deleteProjectLocal(id: string): Promise<void> {
  // The server FKs are ON DELETE SET NULL, but that doesn't bump the rows'
  // updated_at, so a sync pull wouldn't refresh them. Unassign referencing
  // tasks/notes locally (and enqueue) so the cache and server stay in step.
  const [tasks, notes] = await Promise.all([getAll<Task>("tasks"), getAll<Note>("notes")]);
  for (const t of tasks) if (t.projectId === id) await updateTaskLocal(t.id, { projectId: null });
  for (const n of notes) if (n.projectId === id) await updateNoteLocal(n.id, { projectId: null });
  await deleteRecord("project", id);
  await enqueue({ entity: "project", type: "delete", id, ts: Date.now() });
  requestSync();
}

async function upsertDayLogLocal(
  date: string,
  patch: { planned?: boolean; shutdown?: boolean; incPom?: boolean },
): Promise<void> {
  const iso = nowIso();
  const all = await getAll<DayLog>("dayLogs");
  const existing = all.find((d) => d.date === date);
  const log: DayLog = existing ?? {
    id: uid(),
    date,
    doneCount: 0,
    pomCount: 0,
    planned: false,
    shutdown: false,
    createdAt: iso,
    updatedAt: iso,
  };
  if (patch.planned !== undefined) log.planned = patch.planned;
  if (patch.shutdown !== undefined) log.shutdown = patch.shutdown;
  if (patch.incPom) log.pomCount += 1;
  log.updatedAt = iso;
  await putRecord("dayLog", log);
  await enqueue({
    entity: "dayLog",
    type: "upsert",
    id: log.id,
    ts: Date.parse(iso),
    data: {
      date: log.date,
      doneCount: log.doneCount,
      pomCount: log.pomCount,
      planned: log.planned,
      shutdown: log.shutdown,
    },
  });
  requestSync();
}

/* ------------- mutation hooks (same API as before) ------------- */
export function useTaskMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  return {
    create: useMutation({ mutationFn: createTaskLocal, onSuccess: inv }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: TaskInput }) => updateTaskLocal(id, patch),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: deleteTaskLocal, onSuccess: inv }),
  };
}

export function useNoteMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["notes"] });
  return {
    create: useMutation({ mutationFn: createNoteLocal, onSuccess: inv }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: NoteInput }) => updateNoteLocal(id, patch),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: deleteNoteLocal, onSuccess: inv }),
  };
}

export function useProjectMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["projects"] });
  return {
    create: useMutation({ mutationFn: createProjectLocal, onSuccess: inv }),
    update: useMutation({
      mutationFn: ({
        id,
        patch,
      }: {
        id: string;
        patch: Partial<Pick<Project, "name" | "color" | "visibility" | "archived">>;
      }) => updateProjectLocal(id, patch),
      onSuccess: inv,
    }),
    remove: useMutation({
      mutationFn: (id: string) => deleteProjectLocal(id),
      // Unassigning tasks/notes also touches those caches.
      onSuccess: () => {
        inv();
        qc.invalidateQueries({ queryKey: ["tasks"] });
        qc.invalidateQueries({ queryKey: ["notes"] });
      },
    }),
  };
}

export function useDayLogMutations() {
  const qc = useQueryClient();
  return {
    upsert: useMutation({
      mutationFn: ({
        date,
        patch,
      }: {
        date: string;
        patch: { planned?: boolean; shutdown?: boolean; incPom?: boolean };
      }) => upsertDayLogLocal(date, patch),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["dayLogs"] }),
    }),
  };
}
